import React, { useEffect, useMemo, useState } from 'react'
import { t } from 'ttag'
import { Box, Chip, IconButton, InputAdornment, Typography } from '@material-ui/core'
import YumTooltip from '../../components/YumTooltip/YumTooltip'
import YumCopyButton from '../../components/YumCopyButton/YumCopyButton'
import { type MRT_ColumnDef } from 'material-react-table'
import YumButton from '../../components/YumButton/YumButton'
import YumInput from '../../components/YumInput/YumInput'
import YumTable from '../../components/YumTable/YumTable'
import YumDialog from '../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import YumSelect from '../../components/YumSelect/YumSelect'
import { DivisionDto, Role, SignUpCodeItem, UserCompanyDto } from '../../services/api.types'
import { hasCompanyFeatureFlag } from '../../common/helpers/featureFlags'
import YumRemoveButton from '../../components/YumRemoveButton/YumRemoveButton'
import { ReactComponent as OrganizerInactiveIcon } from '../../assets/v3/icons/organizer-inactive.svg'
import { ReactComponent as GroupInactiveIcon } from '../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as DateInactiveIcon } from '../../assets/v3/icons/date-inactive.svg'
import { ReactComponent as EditIcon } from '../../assets/v3/icons/dashboard/edit.svg'
import SignUpCodeStatus from './components/SignUpCodeStatus/SignUpCodeStatus'
import { darkBlue } from '../../styles/material-ui-theme-v2'

type ExpiryOption = 'never' | '1d' | '1w' | '1m' | '1y'

const computeExpiresAt = (option: ExpiryOption): string | null => {
  if (option === 'never') return null
  const date = new Date()
  switch (option) {
    case '1d':
      date.setDate(date.getDate() + 1)
      break
    case '1w':
      date.setDate(date.getDate() + 7)
      break
    case '1m':
      date.setMonth(date.getMonth() + 1)
      break
    case '1y':
      date.setFullYear(date.getFullYear() + 1)
      break
  }
  return date.toISOString()
}

const expiresAtToOption = (expiresAt: string | null): ExpiryOption => {
  if (!expiresAt) return 'never'
  const now = new Date()
  const expires = new Date(expiresAt)
  const diffMs = expires.getTime() - now.getTime()
  if (diffMs <= 0) return 'never'
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  if (diffDays <= 2) return '1d'
  if (diffDays <= 10) return '1w'
  if (diffDays <= 45) return '1m'
  return '1y'
}

// --- Props ---
export interface ISignUpCodeAdminProps {
  managedCompanies: UserCompanyDto[]
  userCompanies: UserCompanyDto[]
  companyDivisions: DivisionDto[]
  codes: SignUpCodeItem[]
  loading: boolean
  creating: boolean
  updating: boolean
  deleting: boolean
  selectedCompanyId: string | null
}

export interface ISignUpCodeAdminDispatchProps {
  getCompanySignUpCodes: (payload: { companyId: string }) => void
  createCompanySignUpCode: (payload: {
    companyId: string
    role: Role
    expiresAt?: string | null
    divisionId?: string | null
    usageLimit?: number | null
  }) => void
  updateCompanySignUpCode: (payload: {
    companyId: string
    signUpCodeId: string
    role?: Role
    expiresAt?: string | null
    divisionId?: string | null
    usageLimit?: number | null
  }) => void
  deleteCompanySignUpCode: (payload: { companyId: string; signUpCodeId: string }) => void
  setSignUpCodeSelectedCompanyId: (payload: string) => void
}

type SignUpCodeAdminProps = ISignUpCodeAdminProps & ISignUpCodeAdminDispatchProps

/**
 * Admin page for managing company sign-up codes.
 *
 * Allows admins to create, edit, and delete sign-up codes
 * that users can use to join a company.
 */
export default function SignUpCodeAdmin(props: SignUpCodeAdminProps) {
  const {
    managedCompanies,
    userCompanies,
    companyDivisions,
    codes,
    loading,
    creating,
    updating,
    deleting,
    selectedCompanyId,
    getCompanySignUpCodes,
    createCompanySignUpCode,
    updateCompanySignUpCode,
    deleteCompanySignUpCode,
    setSignUpCodeSelectedCompanyId,
  } = props

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Form state for create/edit
  const [formRole, setFormRole] = useState<Role>(Role.USER)
  const [formExpiry, setFormExpiry] = useState<ExpiryOption>('never')
  const [formDivisionId, setFormDivisionId] = useState('none')
  const [formUsageLimit, setFormUsageLimit] = useState('')

  // Currently selected code for edit/delete
  const [selectedCode, setSelectedCode] = useState<SignUpCodeItem | null>(null)

  // Select first managed company by default
  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  const showTeamField = hasCompanyFeatureFlag(userCompanies, companyId, 'signUpCodeTeam')

  // Set default company on first mount
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setSignUpCodeSelectedCompanyId(companyId)
    }
  }, [])

  // Load codes whenever selected company changes
  useEffect(() => {
    if (companyId) {
      getCompanySignUpCodes({ companyId })
    }
  }, [companyId])

  // Close create dialog when creating finishes
  useEffect(() => {
    if (!creating && createDialogOpen) {
      setCreateDialogOpen(false)
    }
  }, [creating])

  // Close edit dialog when updating finishes
  useEffect(() => {
    if (!updating && editDialogOpen) {
      setEditDialogOpen(false)
    }
  }, [updating])

  // Close delete dialog when deleting finishes
  useEffect(() => {
    if (!deleting && deleteDialogOpen) {
      setDeleteDialogOpen(false)
    }
  }, [deleting])

  // Handle company selection change
  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newCompanyId = event.target.value as string
    setSignUpCodeSelectedCompanyId(newCompanyId)
    getCompanySignUpCodes({ companyId: newCompanyId })
  }

  // Reset form state
  const resetForm = () => {
    setFormRole(Role.USER)
    setFormExpiry('never')
    setFormDivisionId('none')
    setFormUsageLimit('')
  }

  // Open create dialog
  const handleCreateOpen = () => {
    resetForm()
    setCreateDialogOpen(true)
  }

  // Open edit dialog
  const handleEditOpen = (code: SignUpCodeItem) => {
    setSelectedCode(code)
    setFormRole(code.role)
    setFormExpiry(expiresAtToOption(code.expiresAt))
    setFormDivisionId(code.divisionId || 'none')
    setFormUsageLimit(code.usageLimit != null ? String(code.usageLimit) : '')
    setEditDialogOpen(true)
  }

  // Open delete dialog
  const handleDeleteOpen = (code: SignUpCodeItem) => {
    setSelectedCode(code)
    setDeleteDialogOpen(true)
  }

  // Handle create submit
  const handleCreateSubmit = () => {
    if (!companyId || creating) return
    createCompanySignUpCode({
      companyId,
      role: formRole,
      expiresAt: computeExpiresAt(formExpiry),
      divisionId: formDivisionId !== 'none' ? formDivisionId : null,
      usageLimit: formUsageLimit ? parseInt(formUsageLimit, 10) : null,
    })
  }

  // Handle edit submit
  const handleEditSubmit = () => {
    if (!companyId || !selectedCode || updating) return
    updateCompanySignUpCode({
      companyId,
      signUpCodeId: selectedCode.id,
      role: formRole,
      expiresAt: computeExpiresAt(formExpiry),
      divisionId: formDivisionId !== 'none' ? formDivisionId : null,
      usageLimit: formUsageLimit ? parseInt(formUsageLimit, 10) : null,
    })
  }

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (!companyId || !selectedCode || deleting) return
    deleteCompanySignUpCode({
      companyId,
      signUpCodeId: selectedCode.id,
    })
  }

  // Format timestamp to readable date
  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp)
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return timestamp
    }
  }

  // Find division name by ID
  const getDivisionName = (divisionId: string | null) => {
    if (!divisionId) return '\u2014'
    const division = companyDivisions.find((d) => d.id === divisionId)
    return division ? division.name : divisionId
  }

  // Table column definitions
  const columns = useMemo<MRT_ColumnDef<SignUpCodeItem>[]>(
    () =>
      [
        {
          accessorKey: 'code',
          header: t`Code`,
          Cell: ({ row: { original } }: any) => (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Typography variant="body2" style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                {original.code}
              </Typography>
              <YumCopyButton text={original.code} />
            </Box>
          ),
        },
        {
          id: 'status',
          header: t`Status`,
          size: 120,
          Cell: ({ row: { original } }: any) => (
            <SignUpCodeStatus
              expiresAt={original.expiresAt}
              usageLimit={original.usageLimit}
              usageCount={original.usageCount}
            />
          ),
        },
        {
          accessorKey: 'role',
          header: t`Role`,
          size: 100,
          Cell: ({
            row: {
              original: { role },
            },
          }: any) => (
            <Chip
              label={role === Role.ADMIN ? t`Admin` : t`User`}
              size="small"
              style={{
                backgroundColor: role === 'ADMIN' ? darkBlue : '#f1f1f1',
                color: role === 'ADMIN' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                fontWeight: role === 'ADMIN' ? 500 : 'normal',
                marginBottom: 0,
                textTransform: 'uppercase',
              }}
            />
          ),
        },
        showTeamField
          ? {
              accessorKey: 'divisionId',
              header: t`Team`,
              size: 140,
              Cell: ({ row: { original } }: any) => (
                <Typography variant="body2">{getDivisionName(original.divisionId)}</Typography>
              ),
            }
          : null,
        {
          accessorKey: 'usageCount',
          header: t`Used`,
          size: 80,
          Cell: ({ row: { original } }: any) => (
            <Typography
              variant="body2"
              style={
                original.usageLimit != null && original.usageCount >= original.usageLimit ? { color: 'red' } : undefined
              }
            >
              {original.usageCount}/{original.usageLimit != null ? original.usageLimit : '\u221E'}
            </Typography>
          ),
        },
        {
          accessorKey: 'lastUsedAt',
          header: t`Last used`,
          size: 140,
          Cell: ({ row: { original } }: any) => (
            <Typography variant="body2">{original.lastUsedAt ? formatDate(original.lastUsedAt) : '\u2014'}</Typography>
          ),
        },
        {
          accessorKey: 'expiresAt',
          header: t`Expires`,
          size: 140,
          Cell: ({ row: { original } }: any) => (
            <Typography
              variant="body2"
              style={original.expiresAt && new Date(original.expiresAt) < new Date() ? { color: 'red' } : undefined}
            >
              {original.expiresAt ? formatDate(original.expiresAt) : '\u2014'}
            </Typography>
          ),
        },
        {
          accessorKey: 'createdAt',
          header: t`Created`,
          size: 140,
          Cell: ({ row: { original } }: any) => (
            <Typography variant="body2">{formatDate(original.createdAt)}</Typography>
          ),
        },
        {
          id: 'actions',
          header: '',
          size: 80,
          enableSorting: false,
          muiTableHeadCellProps: { align: 'right' },
          Cell: ({ row: { original } }: any) => (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', width: '100%' }}>
              <YumTooltip title={t`Edit`} arrow placement="top">
                <IconButton onClick={() => handleEditOpen(original)} style={{ borderRadius: '10px' }}>
                  <EditIcon style={{ width: 20, height: 20 }} />
                </IconButton>
              </YumTooltip>
              <YumRemoveButton onClick={() => handleDeleteOpen(original)} />
            </Box>
          ),
        },
      ].filter(Boolean) as MRT_ColumnDef<SignUpCodeItem>[],
    [companyDivisions, showTeamField],
  )

  // Render form fields (shared between create and edit dialogs)
  const renderFormFields = (isEdit: boolean) => (
    <>
      <YumSelect
        label={t`Role`}
        startIcon={OrganizerInactiveIcon}
        value={formRole}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
          const role = e.target.value as Role
          setFormRole(role)
          if (role === Role.ADMIN && (formExpiry === 'never' || formExpiry === '1y')) {
            setFormExpiry('1w')
          }
        }}
        disabled={isEdit ? updating : creating}
        style={{ marginBottom: 12 }}
      >
        <option value={Role.USER}>{t`User`}</option>
        <option value={Role.ADMIN}>{t`Admin`}</option>
      </YumSelect>
      {formRole === Role.ADMIN && (
        <Box
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            backgroundColor: '#fff3e0',
            borderRadius: 8,
          }}
        >
          <Typography variant="body2" style={{ fontSize: 13, color: '#e65100' }}>
            {t`Users who sign up with this code will get admin access to the company.`}
          </Typography>
        </Box>
      )}
      {showTeamField && (
        <YumSelect
          label={t`Team`}
          startIcon={GroupInactiveIcon}
          value={formDivisionId}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => setFormDivisionId(e.target.value as string)}
          disabled={isEdit ? updating : creating}
          style={{ marginBottom: 12 }}
        >
          <option value="none">{t`None`}</option>
          {companyDivisions
            .filter((div) => div.id != null)
            .map((div) => (
              <option key={div.id!} value={div.id!}>
                {div.name}
              </option>
            ))}
        </YumSelect>
      )}
      <YumSelect
        label={t`Valid for`}
        startIcon={DateInactiveIcon}
        value={formExpiry}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => setFormExpiry(e.target.value as ExpiryOption)}
        disabled={isEdit ? updating : creating}
        style={{ marginBottom: 12 }}
      >
        <option value="never" disabled={formRole === Role.ADMIN}>{t`Forever`}</option>
        <option value="1d">{t`1 day`}</option>
        <option value="1w">{t`1 week`}</option>
        <option value="1m">{t`1 month`}</option>
        <option value="1y" disabled={formRole === Role.ADMIN}>{t`1 year`}</option>
      </YumSelect>
      <YumInput
        label={t`Usage limit`}
        type="number"
        startIcon={GroupInactiveIcon}
        value={formUsageLimit}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormUsageLimit(e.target.value)}
        placeholder={t`Unlimited`}
        disabled={isEdit ? updating : creating}
        InputProps={{
          endAdornment: <InputAdornment position="end">{formUsageLimit === '1' ? t`user` : t`users`}</InputAdornment>,
        }}
      />
    </>
  )

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>
          {t`Sign-up codes`}
          {!loading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({codes.length})</span>}
        </h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
            {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
              <option key={keyIndex} value={userCompany.companyId}>
                {userCompany.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>

      {/*<Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
        {t`Manage sign-up codes that users can use to join your company. Codes can be configured with a specific role, team, expiration date, and usage limit.`}
      </Typography>*/}

      {/* Create dialog */}
      <YumDialog open={createDialogOpen}>
        <YumDialogTitle handleClose={() => !creating && setCreateDialogOpen(false)}>
          {t`Create sign-up code`}
        </YumDialogTitle>
        <YumDialogContent>
          <NotificationsWrapper />
          {renderFormFields(false)}
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={() => setCreateDialogOpen(false)} disabled={creating} />
          <YumSubmitButton
            text={t`Create`}
            onClick={handleCreateSubmit}
            disabled={creating}
            submitInProgress={creating}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>

      {/* Edit dialog */}
      <YumDialog open={editDialogOpen}>
        <YumDialogTitle handleClose={() => !updating && setEditDialogOpen(false)}>
          {t`Edit sign-up code`}
        </YumDialogTitle>
        <YumDialogContent>
          <NotificationsWrapper />
          {renderFormFields(true)}
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={() => setEditDialogOpen(false)} disabled={updating} />
          <YumSubmitButton
            text={t`Save`}
            onClick={handleEditSubmit}
            disabled={updating}
            submitInProgress={updating}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>

      {/* Delete confirmation dialog */}
      <YumDialog open={deleteDialogOpen}>
        <YumDialogTitle handleClose={() => !deleting && setDeleteDialogOpen(false)}>
          {t`Delete sign-up code`}
        </YumDialogTitle>
        <YumDialogContent>
          <Typography variant="body2">
            {t`Are you sure you want to delete the sign-up code`} <strong>{selectedCode?.code}</strong>?
          </Typography>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={() => setDeleteDialogOpen(false)} disabled={deleting} />
          <YumSubmitButton
            text={t`Delete`}
            onClick={handleDeleteConfirm}
            disabled={deleting}
            submitInProgress={deleting}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>

      <YumTable
        enableRowNumbers={false}
        columns={columns as any}
        data={codes}
        getRowId={(row: any) => row.id as string}
        state={{ isLoading: loading }}
        renderTopToolbarCustomActions={() => (
          <YumButton color="secondary" size="small" onClick={handleCreateOpen}>
            {t`Create code`}
          </YumButton>
        )}
        initialState={{
          sorting: [{ id: 'createdAt', desc: true }],
          density: 'compact',
          columnPinning: { right: ['actions'] },
        }}
      />
    </Card>
  )
}
